import { NetworkStatus, useQuery } from "@apollo/client";
import { useEffect, useMemo, useState } from "react";
import {
  GetOrgChartOrganizationMembersQueryQuery,
  GetOrgChartOrganizationMembersQueryQueryVariables,
} from "types/graphql-schema";

import useUiPreferenceCache from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import { currentOrganizationVar } from "@cache/cache";
import { assertEdgesNonNull } from "@helpers/helpers";

import getOrgChartOrganizationMembersQuery from "./graphql/get-org-chart-organization-members-query";

const useFetchOrgChart = () => {
  const currentOrganization = currentOrganizationVar();
  const { uiPreferenceCache } = useUiPreferenceCache();
  const [loading, setLoading] = useState<boolean>(true);

  const {
    data,
    networkStatus,
    fetchMore,
    loading: fetching,
  } = useQuery<
    GetOrgChartOrganizationMembersQueryQuery,
    GetOrgChartOrganizationMembersQueryQueryVariables
  >(getOrgChartOrganizationMembersQuery, {
    variables: {
      organizationId: currentOrganization.id,
      userIsActive: uiPreferenceCache.orgChartShowInactiveUsers ? null : true,
    },
  });

  const members = useMemo(() => {
    return data?.organization?.members
      ? assertEdgesNonNull(data.organization.members)
      : [];
  }, [data]);

  useEffect(() => {
    if (networkStatus !== NetworkStatus.ready) return;
    if (!data?.organization?.members?.pageInfo) return;

    // fetch more goals if there are more
    if (data.organization?.members.pageInfo.hasNextPage) {
      fetchMore({
        variables: {
          merge: true,
          after: data?.organization?.members?.pageInfo.endCursor,
        },
      });
    }

    if (data.organization?.members.pageInfo.hasNextPage === false) {
      setLoading(false);
    }
  }, [data, networkStatus, fetchMore]);

  useEffect(() => {
    if (fetching && !loading) {
      setLoading(true);
    }
  }, [fetching, loading]);

  return {
    members,
    loading,
  };
};

export default useFetchOrgChart;
